/**
 * Google Tag Manager Service
 * Handles GTM initialization and event tracking for React SPA
 */

class GTMService {
  constructor() {
    this.gtmId = 'GTM-PZ857V5X';
    this.isInitialized = false;
  }

  /**
   * Initialize Google Tag Manager
   * Call this once when the app starts
   */
  initialize() {
    if (this.isInitialized || typeof window === 'undefined') {
      return;
    }

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];

    // GTM Script injection
    const gtmScript = document.createElement('script');
    gtmScript.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${this.gtmId}');
    `;
    document.head.insertBefore(gtmScript, document.head.firstChild);

    // GTM NoScript fallback
    const gtmNoScript = document.createElement('noscript');
    gtmNoScript.innerHTML = `
      <iframe src="https://www.googletagmanager.com/ns.html?id=${this.gtmId}"
      height="0" width="0" style="display:none;visibility:hidden"></iframe>
    `;
    document.body.insertBefore(gtmNoScript, document.body.firstChild);

    this.isInitialized = true;
    console.log('✅ Google Tag Manager initialized');

    // Send initial page view
    this.pageView(window.location.pathname);
  }

  /**
   * Track page views (for SPA navigation)
   */
  pageView(path, title = document.title) {
    if (!this.isInitialized || typeof window === 'undefined') {
      return;
    }

    window.dataLayer.push({
      event: 'page_view',
      page_path: path,
      page_title: title,
      page_location: window.location.href
    });

    console.log('📊 GTM Page View:', path);
  }

  /**
   * Track custom events
   */
  trackEvent(eventName, parameters = {}) {
    if (!this.isInitialized || typeof window === 'undefined') {
      return;
    }

    window.dataLayer.push({
      event: eventName,
      ...parameters
    });

    console.log('📊 GTM Event:', eventName, parameters);
  }

  /**
   * Track e-commerce events
   */
  trackPurchase(transactionData) {
    if (!this.isInitialized || typeof window === 'undefined') {
      return;
    }

    window.dataLayer.push({
      event: 'purchase',
      ecommerce: {
        transaction_id: transactionData.transaction_id,
        value: transactionData.value,
        currency: transactionData.currency || 'IQD',
        items: transactionData.items
      }
    });

    console.log('📊 GTM Purchase:', transactionData);
  }

  /**
   * Track product views
   */
  trackProductView(productData) {
    if (!this.isInitialized || typeof window === 'undefined') {
      return;
    }

    window.dataLayer.push({
      event: 'view_item',
      ecommerce: {
        currency: 'IQD',
        value: productData.price,
        items: [{
          item_id: productData.id,
          item_name: productData.name,
          item_category: productData.category,
          item_brand: productData.brand,
          price: productData.price,
          quantity: 1
        }]
      }
    });

    console.log('📊 GTM Product View:', productData);
  }

  /**
   * Track add to cart events
   */
  trackAddToCart(productData) {
    if (!this.isInitialized || typeof window === 'undefined') {
      return;
    }

    window.dataLayer.push({
      event: 'add_to_cart',
      ecommerce: {
        currency: 'IQD',
        value: productData.price * productData.quantity,
        items: [{
          item_id: productData.id,
          item_name: productData.name,
          item_category: productData.category,
          item_brand: productData.brand,
          price: productData.price,
          quantity: productData.quantity
        }]
      }
    });

    console.log('📊 GTM Add to Cart:', productData);
  }

  /**
   * Track search events
   */
  trackSearch(searchTerm, resultsCount = 0) {
    if (!this.isInitialized || typeof window === 'undefined') {
      return;
    }

    window.dataLayer.push({
      event: 'search',
      search_term: searchTerm,
      search_results: resultsCount
    });

    console.log('📊 GTM Search:', searchTerm, resultsCount);
  }

  /**
   * Track user registration
   */
  trackRegistration(method = 'email') {
    if (!this.isInitialized || typeof window === 'undefined') {
      return;
    }

    window.dataLayer.push({
      event: 'sign_up',
      method: method
    });

    console.log('📊 GTM Registration:', method);
  }

  /**
   * Track user login
   */
  trackLogin(method = 'email') {
    if (!this.isInitialized || typeof window === 'undefined') {
      return;
    }

    window.dataLayer.push({
      event: 'login',
      method: method
    });

    console.log('📊 GTM Login:', method);
  }
}

// Create and export singleton instance
const gtmService = new GTMService();

export default gtmService;